import { useState } from 'react';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Button,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { FieldValues, useForm } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../../../app/store/configureStore';
import AppTextInput from '../../../app/components/AppTextInput';
import { changePasswordValidationSchema } from './changePasswordValidationSchema';
import { changePassword } from '../accountSlice';
import { toast } from 'react-toastify';

interface ChangePasswordFormValues {
  currentPassword?: string;
  newPassword?: string;
}

const ChangePassword = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
  } = useForm<ChangePasswordFormValues>({
    mode: 'onTouched',
    resolver: yupResolver(changePasswordValidationSchema(t)),
  });

  const dispatch = useAppDispatch();

  const handleSubmitData = async (data: FieldValues) => {
    const { currentPassword, newPassword } = data;
    if (!email || !isValid || errors) {
      toast.error(
        'Password cannot be reset at this time. Please try again later.'
      );
      return;
    }
    try {
      const isPasswordResetSuccessful = await dispatch(
        changePassword({ email, currentPassword, newPassword })
      ).unwrap();
      if (isPasswordResetSuccessful) {
        navigate('/account/profile');
      }
    } catch (error) {
      console.log(error);
    }
  };

  const cancelEdit = () => navigate('/account/profile');

  return (
    <Container
      component={Paper}
      maxWidth="sm"
      sx={{
        px: 1,
        py: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        m: 0,
      }}
    >
      <img
        src="/logo.svg"
        alt="Smartristo Logo"
        style={{
          width: '100px',
          marginBottom: '20px',
        }}
      />
      <Typography component="h1" variant="h5">
        {t('change-password.title')}
      </Typography>
      <Box
        component="form"
        onSubmit={handleSubmit(handleSubmitData)}
        noValidate
        sx={{ mt: 1 }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12}>
            <AppTextInput<ChangePasswordFormValues>
              type={showPassword ? 'text' : 'password'}
              control={control}
              name="currentPassword"
              label={t('change-password.current-password')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <AppTextInput<ChangePasswordFormValues>
              type={showPassword ? 'text' : 'password'}
              control={control}
              name="newPassword"
              label={t('change-password.new-password')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        <LoadingButton
          loading={isSubmitting}
          type="submit"
          fullWidth
          variant="contained"
          sx={{
            mt: 3,
            mb: 2,
          }}
        >
          {t('change-password.title')}
        </LoadingButton>
        <Button fullWidth variant="outlined" onClick={cancelEdit}>
          {t('common.cancel')}
        </Button>
      </Box>
    </Container>
  );
};

export default ChangePassword;
